import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { GoalsPanel } from "@/components/studio/GoalsPanel";
import { RangeControls, WordsChart, bucketize, useStatsRange } from "@/components/studio/WordsChart";
import { api } from "@/lib/api";
import { chapterLabel, fmtMinutes, fmtNum, localDay, readMinutes } from "@/lib/studio";
import { computeStreak, streakParams } from "@/components/studio/streaks";
import { Flame } from "lucide-react";

const GoalBar = ({ label, value, goal, unit = "words", id }) => (
  <div className="rounded-md border border-[#2D3442] bg-[#13171F] p-4" data-testid={`goal-progress-${id}`}>
    <div className="flex items-baseline justify-between"><span className="lf-label">{label}</span>{goal > 0 && <span className="text-xs text-[#A8A090]">{Math.round(Math.max(0, value) / goal * 100)}%</span>}</div>
    <div className="mt-2 font-display text-3xl text-[#F3EBDD]" data-testid={`goal-progress-${id}-value`}>{fmtNum(value)} <span className="text-sm text-[#A8A090]">{goal > 0 ? `/ ${fmtNum(goal)} ${unit}` : unit}</span></div>
    {goal > 0 && <Progress value={Math.min(100, Math.max(0, value) / goal * 100)} className="mt-3 h-1.5" />}
  </div>
);

const Check = ({ ok }) => (ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10B981]" /> : <AlertTriangle className="h-3.5 w-3.5 text-[#FBBF24]" />);

const ChapterTable = ({ chapters, goals, link }) => (
  <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-5" data-testid="chapter-stats">
    <div className="lf-label">Chapters · reading time at {goals.wpm} words per minute</div>
    <table className="mt-3 w-full text-sm">
      <thead><tr className="text-left text-xs text-[#A8A090]"><th className="py-2 font-normal">Chapter</th><th className="font-normal">Words</th><th className="font-normal">Reading time</th><th className="font-normal">Length goal</th><th className="font-normal">Time goal</th></tr></thead>
      <tbody>
        {chapters.map((c, i) => {
          const m = readMinutes(c.word_count, goals.wpm);
          return (
            <tr key={c.id} className="border-t border-[#2D3442] text-[#F3EBDD]" data-testid={`chapter-stat-${c.id}`}>
              <td className="py-2"><Link to={link(c)} className="hover:text-[#F59E0B]">{chapterLabel(i + 1, c.title)}</Link></td>
              <td data-testid={`chapter-stat-words-${c.id}`}>{fmtNum(c.word_count)}</td>
              <td data-testid={`chapter-stat-time-${c.id}`}>{fmtMinutes(m)}</td>
              <td>{goals.chapter_words > 0 ? <span className="flex items-center gap-1.5"><Check ok={c.word_count >= goals.chapter_words} />{Math.round(c.word_count / goals.chapter_words * 100)}%</span> : "—"}</td>
              <td>{goals.chapter_minutes > 0 ? <span className="flex items-center gap-1.5"><Check ok={m >= goals.chapter_minutes} />{m} / {goals.chapter_minutes} min</span> : "—"}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
    {chapters.length === 0 && <p className="mt-3 text-xs text-[#A8A090]">No chapters yet.</p>}
  </section>
);

export const StoryStats = ({ sid, chapterLink }) => {
  const qc = useQueryClient();
  const r = useStatsRange();
  const { data } = useQuery({ queryKey: ["story-stats", sid, r.params], queryFn: () => api.get(`/stories/${sid}/stats`, { params: r.params }).then((x) => x.data) });
  const { data: today } = useQuery({ queryKey: ["story-stats-today", sid], queryFn: () => { const s = new Date(); s.setHours(0, 0, 0, 0); return api.get(`/stories/${sid}/stats`, { params: { start: s.toISOString().slice(0, 13), end: new Date().toISOString().slice(0, 13) } }).then((x) => x.data); } });
  const { data: long } = useQuery({ queryKey: ["story-stats", sid, "streak"], queryFn: () => api.get(`/stories/${sid}/stats`, { params: streakParams() }).then((x) => x.data) });
  if (!data) return null;
  const { rows, gran } = bucketize(data.hours, r.start, r.end, r.gran);
  const g = data.goals;
  const streak = computeStreak(long?.hours, g.daily_words);
  const todayWords = (today?.hours || []).reduce((a, h) => a + h.words, 0);
  const period = data.hours.reduce((a, h) => a + h.words, 0);
  const totalMin = readMinutes(data.total_words, g.wpm);
  return (
    <div className="space-y-6" data-testid="story-stats-panel">
      {g.daily_words > 0 && <div className="flex items-center gap-2 text-sm text-[#F3EBDD]" data-testid="streak-counter"><Flame className={`h-5 w-5 ${streak ? "text-[#F59E0B]" : "text-[#A8A090]"}`} /><b>{streak}</b> day{streak === 1 ? "" : "s"} in a row at {fmtNum(g.daily_words)}+ words</div>}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <GoalBar id="total" label="Story length" value={data.total_words} goal={g.total_words} />
        <GoalBar id="today" label={`Today · ${localDay(new Date())}`} value={todayWords} goal={g.daily_words} />
        <GoalBar id="period" label="This period (net)" value={period} />
        <GoalBar id="read" label="Full read" value={totalMin} unit="min" />
      </div>
      <section className="rounded-md border border-[#2D3442] bg-[#0F131A] p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3"><div className="lf-label">Words written (net change per {gran})</div><RangeControls r={r} effective={gran} /></div>
        <WordsChart rows={rows} goal={gran === "day" ? g.daily_words : 0} />
      </section>
      <ChapterTable chapters={data.chapters} goals={g} link={chapterLink} />
      <GoalsPanel key={JSON.stringify(g)} sid={sid} goals={g} onSaved={() => { qc.invalidateQueries({ queryKey: ["story-stats", sid] }); qc.invalidateQueries({ queryKey: ["story", sid] }); }} />
    </div>
  );
};
